const claimFiles = ref([]);

export const useInsuranceClaim = () => {
  const { api } = useApi();
  const { openAlert } = useModal();

  const form = useState("insurance-claim-form", () => ({
    name: "",
    phone: "",
    email: "",
    title: "",
    description: "",
    agreePersonal: false,
    agreeThird: false,
  }));

  const submitted = useState("insurance-claim-submitted", () => ({
    email: "",
    title: "",
  }));

  const files = claimFiles;
  const busy = useState("insurance-claim-busy", () => false);
  const claimList = useState("insurance-claim-list", () => []);

  const mapClaimItem = (item) => ({
    id: String(item?.insIdx || ""),
    userName: item?.userName || "",
    phoneNum: item?.phoneNum || "",
    email: item?.email || "",
    title: item?.title || "",
    content: item?.content || "",
    rawDate: item?.createDt || "",
    files: Array.isArray(item?.files)
      ? item.files.map((file) => ({
          idx: file?.idx,
          name: file?.fileName || "",
          link: file?.link || "",
        }))
      : [],
  });

  const claimAsset = (file) => {
    const base = useRuntimeConfig().app.baseURL || "/view";
    return `${String(base).replace(/\/$/, "")}/assets/images/${file}`;
  };

  const digitsPhone = () => String(form.value.phone || "").replace(/\D/g, "");

  const errors = computed(() => ({
    name: String(form.value.name || "").trim().length >= 2 ? "" : "이름을 2자 이상 입력해 주세요.",
    phone: /^01[016789]\d{7,8}$/.test(digitsPhone()) ? "" : "올바른 연락처를 입력해 주세요.",
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.value.email || "").trim())
      ? ""
      : "올바른 이메일 주소를 입력해 주세요.",
    title: String(form.value.title || "").trim() ? "" : "사고 제목을 입력해 주세요.",
    description: String(form.value.description || "").trim() ? "" : "사고 내용을 자세히 입력해 주세요.",
  }));

  const hasFormError = computed(() => Object.values(errors.value).some(Boolean));
  const agreementsOk = computed(() => form.value.agreePersonal && form.value.agreeThird);

  const reset = () => {
    form.value = {
      name: "",
      phone: "",
      email: "",
      title: "",
      description: "",
      agreePersonal: false,
      agreeThird: false,
    };
    files.value = [];
    busy.value = false;
  };

  const createInsFile = async (insIdx, uploadFiles) => {
    if (!insIdx || !uploadFiles?.length) return true;

    try {
      const body = new FormData();
      body.append("insIdx", String(insIdx));
      body.append("fileName", uploadFiles.map((file) => file.name).join(","));
      uploadFiles.forEach((file) => body.append("fileData", file));

      const authState = useAuthState();
      const config = useRuntimeConfig();
      const response = await fetch(`${config.public.apiServer}/api/ins/createInsFile`, {
        method: "POST",
        body,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${authState.token.value}`,
        },
      });
      if (!response.ok) return false;
      const data = await response.json();
      return data.resCd === "0000";
    } catch (error) {
      console.error("createInsFile error:", error);
      return false;
    }
  };

  const submit = async () => {
    if (busy.value) return false;
    busy.value = true;
    try {
      const response = await api("/api/ins/createInsApply", {
        userName: String(form.value.name || "").trim(),
        phoneNum: digitsPhone(),
        email: String(form.value.email || "").trim(),
        title: String(form.value.title || "").trim(),
        content: String(form.value.description || "").trim(),
      });
      if (response?.resCd !== "0000") {
        throw new Error(response?.resMsg || "보험금 신청에 실패했습니다.");
      }

      const insIdx = response.result?.insIdx;
      if (files.value.length) {
        const saved = await createInsFile(insIdx, files.value);
        if (!saved) {
          throw new Error("파일저장 오류");
        }
      }

      submitted.value = {
        email: String(form.value.email || "").trim(),
        title: String(form.value.title || "").trim(),
      };
      reset();
      return true;
    } catch (error) {
      await openAlert({ title: "알림", message: error.message || "보험금 신청에 실패했습니다." });
      return false;
    } finally {
      busy.value = false;
    }
  };

  return {
    form,
    files,
    submitted,
    busy,
    claimList,
    errors,
    hasFormError,
    agreementsOk,
    claimAsset,
    mapClaimItem,
    reset,
    submit,
  };
};
